import { useState } from 'react';
import type { RenderedVideo } from '@/types/video-enhancer';
import { CreditOperation } from '@/hooks/useCredits';

// Global singleton to track completed jobs across all instances
const globalCompletedJobs = new Set<string>();

interface UseAudioEnhancementParams {
  renderedVideos: RenderedVideo[];
  setRenderedVideos: React.Dispatch<React.SetStateAction<RenderedVideo[]>>;
  toast: (opts: { title: string; description?: string; variant?: string }) => void;
  requireCreditConfirmation: (
    operationType: CreditOperation,
    operationName: string,
    onConfirm: () => void
  ) => void;
  CREDIT_OPERATIONS: { AUDIO_ENHANCEMENT: CreditOperation };
}

export function useAudioEnhancement({
  renderedVideos,
  setRenderedVideos,
  toast,
  requireCreditConfirmation,
  CREDIT_OPERATIONS,
}: UseAudioEnhancementParams) {
  const [activeEnhancementJobs, setActiveEnhancementJobs] = useState<Set<string>>(new Set());

  const markAsFailed = (videoId: string, jobId: string, message: string) => {
    setActiveEnhancementJobs(prev => {
      const newSet = new Set(prev);
      newSet.delete(jobId);
      return newSet;
    });

    setRenderedVideos(prev => prev.map(v =>
      v.id === videoId
        ? {
            ...v,
            audioEnhancementStatus: 'failed' as const,
            audioEnhancementJobId: undefined,
            audioEnhancementProgress: 0
          }
        : v
    ));

    toast({
      title: "Audio enhancement failed",
      description: message,
      variant: "destructive"
    });
  };

  const handleEnhanceAudio = async (video: RenderedVideo, mode: 'enhance' | 'denoise' | 'isolate' = 'enhance') => {
    const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');

    const modeLabels = { enhance: 'Enhancing', denoise: 'Denoising', isolate: 'Isolating speech in' };

    try {
      toast({
        title: `${modeLabels[mode]} audio`,
        description: `Improving audio quality for ${video.name}...`,
      });

      // Use videoUrl or url field
      const videoUrl = video.videoUrl || video.url;
      const videoKey = video.savedS3Key;

      const response = await fetch('/api/enhance-video-audio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          videoUrl: videoUrl,
          videoKey: videoKey,
          mode: mode
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Audio enhancement failed');
      }

      const result = await response.json();
      console.log('Audio enhancement API response:', result);

      if (result.jobId) {
        toast({
          title: "Audio enhancement started",
          description: `Processing audio for ${video.name}. This may take a few minutes...`,
        });

        // Update video with job ID and processing status
        setRenderedVideos(prev => prev.map(v =>
          v.id === video.id
            ? {
                ...v,
                audioEnhancementJobId: result.jobId,
                audioEnhancementStatus: 'processing' as const,
                audioEnhancementProgress: 0
              }
            : v
        ));

        // Start polling
        pollAudioEnhancement(result.jobId, video.id);
      }
    } catch (error: any) {
      console.error('Audio enhancement error:', error);
      toast({
        title: "Audio enhancement failed",
        description: error.message || 'Failed to start audio enhancement',
        variant: "destructive"
      });
    }
  };

  const executeEnhanceAudio = (video: RenderedVideo, mode: 'enhance' | 'denoise' | 'isolate' = 'enhance') => {
    const modeNames = { enhance: 'Audio Enhancement', denoise: 'Audio Denoise (Gentle)', isolate: 'Speech Isolation' };
    requireCreditConfirmation(
      CREDIT_OPERATIONS.AUDIO_ENHANCEMENT,
      modeNames[mode],
      () => handleEnhanceAudio(video, mode)
    );
  };

  const pollAudioEnhancement = async (jobId: string, videoId: string) => {
    // Check if already polling this job
    if (activeEnhancementJobs.has(jobId)) {
      return;
    }

    setActiveEnhancementJobs(prev => new Set([...prev, jobId]));

    let attempts = 0;
    let consecutiveErrors = 0;
    const maxAttempts = 120; // 10 minutes max (120 * 5s)
    const maxConsecutiveErrors = 5;

    const pollInterval = setInterval(async () => {
      try {
        attempts++;

        const response = await fetch(`/api/enhance-video-audio/status/${jobId}`);
        const result = await response.json();

        // Reset consecutive error counter on successful fetch
        consecutiveErrors = 0;

        console.log(`Polling audio enhancement ${jobId}:`, result);

        if (result.status === 'completed') {
          clearInterval(pollInterval);
          setActiveEnhancementJobs(prev => {
            const newSet = new Set(prev);
            newSet.delete(jobId);
            return newSet;
          });

          // Prevent duplicate processing
          if (globalCompletedJobs.has(jobId)) {
            return;
          }
          globalCompletedJobs.add(jobId);

          // Update video with enhanced audio URL (REPLACE original as default playback)
          setRenderedVideos(prev => prev.map(v => {
            if (v.id === videoId) {
              // Store original URL in downloadUrl if not already set
              const originalUrl = v.url || v.videoUrl;

              return {
                ...v,
                url: result.videoUrl, // Enhanced version becomes primary URL
                videoUrl: result.videoUrl, // Also update videoUrl field
                downloadUrl: v.downloadUrl || originalUrl, // Preserve original for download
                enhancedAudioUrl: result.videoUrl,
                enhancedAudioKey: result.s3Key,
                audioEnhancementStatus: 'completed' as const,
                audioEnhancementProgress: 100,
                audioEnhancementJobId: undefined,
                pipelineStage: 'complete' as const,
              };
            }
            return v;
          }));

          // Find the video to get its name for the toast
          const video = renderedVideos.find(v => v.id === videoId);
          toast({
            title: "Audio enhancement complete!",
            description: `Enhanced audio is now available for ${video?.name || 'your video'}`,
          });

        } else if (result.status === 'failed' || result.status === 'not_found') {
          clearInterval(pollInterval);
          markAsFailed(videoId, jobId, result.error || 'Processing failed');

        } else if (result.status === 'processing' || result.status === 'processing_upload') {
          // Update progress
          const progress = result.progress || 0;
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? { ...v, audioEnhancementProgress: progress }
              : v
          ));
        }

        // Check for timeout
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          markAsFailed(videoId, jobId, 'Audio enhancement timed out after 10 minutes');
        }

      } catch (error) {
        console.error('Frontend polling error:', error);
        consecutiveErrors++;

        if (consecutiveErrors >= maxConsecutiveErrors) {
          clearInterval(pollInterval);
          markAsFailed(videoId, jobId, 'Lost connection to audio enhancement service');
          return;
        }

        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          markAsFailed(videoId, jobId, 'Audio enhancement timed out after 10 minutes');
        }
      }
    }, 5000); // Poll every 5 seconds
  };

  return {
    executeEnhanceAudio,
    handleEnhanceAudio,
    activeEnhancementJobs,
  };
}
